import Component from "../../lib/Component.js";
import ScrollInView from "./ScrollInView.js";

/**
 * Section navigator for main page - highlights active section and provides quick navigation
 * @param {{sectionLinks: HTMLAnchorElement[]}} param0
 * @returns
 */
const SectionNavigator = ({ sectionLinks }) => {
  /** @type {{[sectionId: string]: HTMLAnchorElement}} */
  const sectionAnchors = {};
  sectionLinks.forEach((link) => {
    const sectionId = link.getAttribute("href").replace("#", "");
    sectionAnchors[sectionId] = link;
  });

  /** @type {{[sectionId: string]: HTMLElement}} */
  const sections = {};

  // Find all sections with anchors
  document.querySelectorAll(".item-list-section-anchor").forEach((sectionAnchor) => {
    const sectionId = sectionAnchor.getAttribute("id");
    if (sectionId && sectionAnchors[sectionId]) {
      const section = sectionAnchor.closest("section") || sectionAnchor.parentElement.querySelector("section");
      if (section) {
        sections[sectionId] = section;
      }
    }
  });

  /**
   * @param {HTMLElement} section
   * @return {HTMLAnchorElement | null}
   */
  const findLinkBySection = (section) => {
    for (const [sectionId, sectionElement] of Object.entries(sections)) {
      if (sectionElement === section) {
        return sectionAnchors[sectionId];
      }
    }
    return null;
  };

  // Note: Unlike MonthNavigator, we use fixed CSS rules for anchor positioning
  // on main page due to the complex mobile ad layout. See main.scss for
  // .item-list-section-anchor rules that handle mobile vs desktop correctly.

  let scrollingTimeout = null;
  let scrollStarted = null;
  let scrollEndTimeout = null;

  const handleScroll = () => {
    if (!scrollStarted) {
      scrollStarted = Date.now();
    }
    if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
    scrollEndTimeout = window.setTimeout(() => {
      scrollStarted = null;
    }, 200);

    if (Date.now() - scrollStarted > 500) {
      sectionLinks.forEach((link) => link.parentElement.classList.add("scrolling"));
    }

    if (scrollingTimeout) clearTimeout(scrollingTimeout);
    scrollingTimeout = window.setTimeout(() => {
      sectionLinks.forEach((link) => link.parentElement.classList.remove("scrolling"));
    }, 1000);
  };

  const observables = [];
  Object.values(sections).forEach((section) => {
    if (section) observables.push(section);
  });

  const scrollInView = ScrollInView({
    componentName: "SectionNavigator.ScrollInView",
    observables: observables,
    onEnter: (section) => {
      const link = findLinkBySection(section);
      if (link) {
        sectionLinks.forEach((l) => l.parentElement.classList.remove("active"));
        link.parentElement.classList.add("active");
      }
    },
    options: {
      threshold: [0, 0.1, 0.5, 1.0],
      rootMargin: "-30% 0px -70% 0px",
    },
  });

  const handleSectionLinkClick = (event) => {
    event.preventDefault();
    const sectionId = event.target.closest("a").href.split("#")[1];
    const anchor = document.getElementById(sectionId);
    if (anchor) {
      // Use getBoundingClientRect to get actual current position
      const rect = anchor.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      // Calculate absolute position in document
      const targetY = rect.top + scrollTop;

      // Visual offset to account for fixed headers and mobile ad zone
      // Mobile: negative offset positions anchor above viewport top (hidden by header/ad zone)
      // Desktop: positive offset for breathing room below header
      const visualOffset = window.innerWidth >= 1200 ? 36 : -48;

      window.scrollTo({
        top: targetY - visualOffset,
        behavior: "smooth",
      });
    }
  };

  return new Component({
    onMount: () => {
      if (observables.length > 0) {
        scrollInView.mount();
      }
      document.addEventListener("scroll", handleScroll);
      sectionLinks.forEach((link) => link.addEventListener("click", handleSectionLinkClick));
    },
    onUnmount: () => {
      scrollInView.unmount();
      document.removeEventListener("scroll", handleScroll);
      sectionLinks.forEach((link) => link.removeEventListener("click", handleSectionLinkClick));
    },
  });
};

export default SectionNavigator;
